import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Settings,
  FileText,
  Wallet,
  AlertTriangle,
  Bell,
  Eye,
  Hash,
  Clock,
  Receipt,
  Truck,
  Table,
  LayoutGrid,
  CheckCircle,
  CheckSquare,
  Square,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import ModuleConfigPageLayout from '../../shared/components/settings/ModuleConfigPageLayout';
import QawaemErpCrudModal from '../../products/saudi/components/QawaemErpCrudModal';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { getBrandVatRate, resolveLiveVatRate, getBrandDefaultCurrency } from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {
  DEFAULT_FACTURATION_CONFIG,
  normalizeFacturationConfig,
} from './facturationConfigDefaults';
import { syncFacturationConfigToSettings } from './useFacturationConfig';

const NS = 'modules.facturation.pages.facturationConfigPage';

function ConfigSection({ icon: Icon, title, hint, children, cfgTheme }) {
  return (
    <div className={cfgTheme.card}>
      <h3 className={cfgTheme.cardHeading}>
        <Icon className={`h-6 w-6 shrink-0 ${isSaudiBrand() ? 'text-[#a67c3d]' : 'text-blue-400'}`} />
        {title}
      </h3>
      {hint ? <p className={`mb-6 ${cfgTheme.subtitle}`}>{hint}</p> : <div className="mb-4" />}
      {children}
    </div>
  );
}

function ConfigCheckbox({ checked, onChange, label, cfgTheme }) {
  return (
    <label className={`flex items-center gap-3 cursor-pointer ${cfgTheme.label}`}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className={`w-5 h-5 rounded focus:ring-[#6e9b6e]/45 ${isSaudiBrand() ? 'text-[#5a855a]' : 'text-blue-600 focus:ring-blue-500'}`}
      />
      <span>{label}</span>
    </label>
  );
}

function PrefixField({ label, value, onChange, example, cfgTheme, p }) {
  return (
    <div className="space-y-2">
      <label className={cfgTheme.label}>{label}</label>
      <input
        type="text"
        className={cfgTheme.input}
        value={value || ''}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        maxLength={5}
        style={{ textTransform: 'uppercase' }}
      />
      <p className={cfgTheme.helpText}>{p('prefix_example', { code: example })}</p>
    </div>
  );
}

function DelayField({ label, value, onChange, cfgTheme, p }) {
  return (
    <div className="space-y-2">
      <label className={cfgTheme.label}>{label}</label>
      <div className={cfgTheme.daysInputRow}>
        <input
          type="number"
          min={0}
          className={cfgTheme.daysInput}
          value={value ?? 0}
          onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
        />
        <span className={cfgTheme.daysSuffix}>{p('days_suffix')}</span>
      </div>
    </div>
  );
}

const FacturationConfigPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const qawaem = isSaudiBrand();
  const cfgTheme = useModuleConfigTheme();
  const { isDashboardLightMode } = useGlobalTheme();
  const { patchSettings, settings } = useSettings();
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const [facturationConfig, setFacturationConfig] = useState(DEFAULT_FACTURATION_CONFIG);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('documents');
  const [showTableConfigModal, setShowTableConfigModal] = useState(false);

  const tvaSelectOptions = useMemo(() => {
    const standardVat = String(Math.round(resolveLiveVatRate(settings)));
    return qawaem
      ? [
        { value: standardVat, label: p('tva_option_normal'), displayValue: `${standardVat}%` },
        { value: '0', label: p('tva_option_exempt'), displayValue: '0%' },
      ]
      : [
        { value: '19', label: p('tva_option_normal_19'), displayValue: '19%' },
        { value: '9', label: p('tva_option_reduced_9'), displayValue: '9%' },
        { value: '0', label: p('tva_option_not_subject'), displayValue: '0%' },
      ];
  }, [qawaem, p, settings?.taxes?.tva]);

  const paymentMethodOptions = useMemo(() => [
    { value: 'cash', label: p('payment_method_cash') },
    { value: 'transfer', label: p('payment_method_transfer') },
    { value: 'check', label: p('payment_method_check') },
    { value: 'card', label: p('payment_method_card') },
  ], [p]);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await localApiAdapter.get('/facturation/config');
      const raw = response?.data !== undefined ? response.data : response;
      const merged = normalizeFacturationConfig(raw || {});
      setFacturationConfig(merged);
      syncFacturationConfigToSettings(merged, patchSettings, settingsRef.current);
    } catch (error) {
      console.error('Error loading facturation config:', error);
      const fallback = normalizeFacturationConfig({});
      setFacturationConfig(fallback);
      syncFacturationConfigToSettings(fallback, patchSettings, settingsRef.current);
    } finally {
      setLoading(false);
    }
  }, [patchSettings]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const updateConfig = (key, value) => {
    setFacturationConfig((prev) => {
      const next = normalizeFacturationConfig({ ...prev, [key]: value });
      syncFacturationConfigToSettings(next, patchSettings, settingsRef.current);
      return next;
    });
  };

  const updateNested = (parentKey, childKey, value) => {
    setFacturationConfig((prev) => {
      const next = normalizeFacturationConfig({
        ...prev,
        [parentKey]: { ...(prev[parentKey] || {}), [childKey]: value },
      });
      syncFacturationConfigToSettings(next, patchSettings, settingsRef.current);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const payload = normalizeFacturationConfig(facturationConfig);
      await localApiAdapter.put('/facturation/config', payload);
      syncFacturationConfigToSettings(payload, patchSettings, settingsRef.current);
      setFacturationConfig(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving facturation config:', error);
      setSaveError(p('save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(p('reset_confirm'))) return;
    try {
      const resetConfig = normalizeFacturationConfig({});
      setFacturationConfig(resetConfig);
      await localApiAdapter.put('/facturation/config', resetConfig);
      syncFacturationConfigToSettings(resetConfig, patchSettings, settingsRef.current);
    } catch (error) {
      console.error('Error resetting facturation config:', error);
      alert(p('reset_error'));
    }
  };

  const documentsTab = (
    <div className="space-y-6">
      <ConfigSection icon={Receipt} title={p('taxes_section_title')} hint={p('taxes_section_subtitle')} cfgTheme={cfgTheme}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <label style={cfgTheme.labelStyle}>{p('tva_label')}</label>
            <CustomSelect
              creatable={!qawaem}
              autoSelect={false}
              placeholder={p('tva_placeholder')}
              suffix="%"
              options={tvaSelectOptions}
              value={String(facturationConfig.defaultTaxRate ?? getBrandVatRate())}
              onChange={(val) => {
                const cleaned = String(val ?? '').trim().replace(/%/g, '').replace(',', '.');
                const n = parseFloat(cleaned);
                updateConfig('defaultTaxRate', Number.isFinite(n) ? n : getBrandVatRate());
              }}
            />
            <small className={cfgTheme.hint}>{qawaem ? p('tva_hint_saudi') : p('tva_hint')}</small>
          </div>
          <div className="space-y-2">
            <label style={cfgTheme.labelStyle}>{p('vat_mode_label')}</label>
            <CustomSelect
              options={[
                { value: 'exclusive', label: p('vat_mode_exclusive') },
                { value: 'inclusive', label: p('vat_mode_inclusive') },
              ]}
              value={facturationConfig.vatMode || 'exclusive'}
              onChange={(val) => updateConfig('vatMode', val)}
            />
            <small className={cfgTheme.hint}>{p('vat_mode_hint')}</small>
          </div>
          <div className="flex flex-col gap-3 justify-end">
            <ConfigCheckbox
              checked={facturationConfig.showTva !== false}
              onChange={(v) => updateConfig('showTva', v)}
              label={p('show_tva_default')}
              cfgTheme={cfgTheme}
            />
            <ConfigCheckbox
              checked={facturationConfig.autoCalculate !== false}
              onChange={(v) => updateConfig('autoCalculate', v)}
              label={p('auto_calculate')}
              cfgTheme={cfgTheme}
            />
          </div>
        </div>
        {qawaem ? (
          <div className="mt-6 space-y-2">
            <label style={cfgTheme.labelStyle}>{p('zatca_title')}</label>
            <div style={cfgTheme.infoBox}>
              <p style={cfgTheme.infoText}>{p('zatca_hint')}</p>
              <p style={cfgTheme.accentText}>{p('default_currency_locked', { currency: getBrandDefaultCurrency() })}</p>
            </div>
          </div>
        ) : null}
      </ConfigSection>

      <ConfigSection icon={Hash} title={p('section_numbering')} hint={p('section_numbering_hint')} cfgTheme={cfgTheme}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <ConfigCheckbox
            checked={facturationConfig.autoGenerateDocumentNumber !== false}
            onChange={(v) => updateConfig('autoGenerateDocumentNumber', v)}
            label={p('auto_generate_document_number')}
            cfgTheme={cfgTheme}
          />
          <ConfigCheckbox
            checked={facturationConfig.requireApproval === true}
            onChange={(v) => updateConfig('requireApproval', v)}
            label={p('require_approval')}
            cfgTheme={cfgTheme}
          />
          <ConfigCheckbox
            checked={facturationConfig.requireClient !== false}
            onChange={(v) => updateConfig('requireClient', v)}
            label={p('require_client')}
            cfgTheme={cfgTheme}
          />
          <ConfigCheckbox
            checked={facturationConfig.requireLineItems !== false}
            onChange={(v) => updateConfig('requireLineItems', v)}
            label={p('require_line_items')}
            cfgTheme={cfgTheme}
          />
        </div>
        <h4 className={cfgTheme.sectionTitle}>
          <span className="text-green-400">⬆</span> {p('section_sales')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <PrefixField label={p('prefix_quote')} value={facturationConfig.documentPrefixes?.quote} onChange={(v) => updateNested('documentPrefixes', 'quote', v)} example="DEV" cfgTheme={cfgTheme} p={p} />
          <PrefixField label={p('prefix_proforma')} value={facturationConfig.documentPrefixes?.proforma} onChange={(v) => updateNested('documentPrefixes', 'proforma', v)} example="PRO" cfgTheme={cfgTheme} p={p} />
          <PrefixField label={p('prefix_invoice')} value={facturationConfig.documentPrefixes?.invoice} onChange={(v) => updateNested('documentPrefixes', 'invoice', v)} example="FAC" cfgTheme={cfgTheme} p={p} />
          <PrefixField label={p('prefix_delivery')} value={facturationConfig.documentPrefixes?.delivery} onChange={(v) => updateNested('documentPrefixes', 'delivery', v)} example="BL" cfgTheme={cfgTheme} p={p} />
          <PrefixField label={p('prefix_credit_note')} value={facturationConfig.documentPrefixes?.creditNote} onChange={(v) => updateNested('documentPrefixes', 'creditNote', v)} example="NC" cfgTheme={cfgTheme} p={p} />
          <PrefixField label={p('prefix_payment_receipt')} value={facturationConfig.documentPrefixes?.paymentReceipt} onChange={(v) => updateNested('documentPrefixes', 'paymentReceipt', v)} example="RP" cfgTheme={cfgTheme} p={p} />
        </div>
      </ConfigSection>

      <ConfigSection icon={Clock} title={p('delays_default_title')} hint={p('delays_default_subtitle')} cfgTheme={cfgTheme}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DelayField label={p('delay_quote_valid_until')} value={facturationConfig.documentDelays?.quote} onChange={(v) => updateNested('documentDelays', 'quote', v)} cfgTheme={cfgTheme} p={p} />
          <DelayField label={p('delay_invoice_due')} value={facturationConfig.documentDelays?.invoice} onChange={(v) => updateNested('documentDelays', 'invoice', v)} cfgTheme={cfgTheme} p={p} />
          <DelayField label={p('delay_delivery_expected')} value={facturationConfig.documentDelays?.delivery} onChange={(v) => updateNested('documentDelays', 'delivery', v)} cfgTheme={cfgTheme} p={p} />
          <DelayField label={p('delay_proforma_acceptance')} value={facturationConfig.documentDelays?.proforma} onChange={(v) => updateNested('documentDelays', 'proforma', v)} cfgTheme={cfgTheme} p={p} />
          <DelayField label={p('delay_payment_receipt')} value={facturationConfig.documentDelays?.paymentReceipt} onChange={(v) => updateNested('documentDelays', 'paymentReceipt', v)} cfgTheme={cfgTheme} p={p} />
        </div>
      </ConfigSection>

      <ConfigSection icon={Table} title={p('table_config')} hint={p('table_type_hint')} cfgTheme={cfgTheme}>
        <div className="flex justify-between items-center gap-4 flex-wrap">
          <div className="space-y-1">
            <p className={cfgTheme.subtitle}>{p('table_type_label')}: {p(`table_type_${facturationConfig.tableType || 'standard'}`)}</p>
            <p className={cfgTheme.helpText}>{p('default_unit_label')}: {facturationConfig.defaultUnit}</p>
          </div>
          <button type="button" onClick={() => setShowTableConfigModal(true)} className={cfgTheme.btnGhost}>
            <Settings className="h-5 w-5" />
            {p('table_config')}
          </button>
        </div>
      </ConfigSection>
    </div>
  );

  const paymentsTab = (
    <ConfigSection icon={Wallet} title={p('tab_payments')} hint={p('tab_payments_hint')} cfgTheme={cfgTheme}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="space-y-2">
          <label style={cfgTheme.labelStyle}>{p('default_payment_method')}</label>
          <CustomSelect
            options={paymentMethodOptions}
            value={facturationConfig.defaultPaymentMethod || 'cash'}
            onChange={(val) => updateConfig('defaultPaymentMethod', val)}
          />
        </div>
        <div className="space-y-2">
          <label style={cfgTheme.labelStyle}>{p('default_payment_terms')}</label>
          <div className={cfgTheme.daysInputRow}>
            <input
              type="number"
              min={0}
              className={cfgTheme.daysInput}
              value={facturationConfig.defaultPaymentTermsDays ?? 30}
              onChange={(e) => updateConfig('defaultPaymentTermsDays', parseInt(e.target.value, 10) || 0)}
            />
            <span className={cfgTheme.daysSuffix}>{p('days_suffix')}</span>
          </div>
          <small className={cfgTheme.hint}>{p('default_payment_terms_hint')}</small>
        </div>
        <div className="space-y-2">
          <label style={cfgTheme.labelStyle}>{p('late_payment_fee')}</label>
          <CustomNumberInput
            min={0}
            step={0.01}
            decimals={2}
            value={facturationConfig.latePaymentFee || 0}
            onChange={(value) => updateConfig('latePaymentFee', parseFloat(value) || 0)}
          />
        </div>
        <div className="space-y-2">
          <label style={cfgTheme.labelStyle}>{p('reminder_days_before')}</label>
          <input
            type="number"
            min={0}
            className={cfgTheme.input}
            value={facturationConfig.reminderDaysBefore ?? 3}
            onChange={(e) => updateConfig('reminderDaysBefore', parseInt(e.target.value, 10) || 0)}
          />
          <small className={cfgTheme.hint}>{p('reminder_days_before_hint')}</small>
        </div>
        <ConfigCheckbox
          checked={facturationConfig.allowPartialPayments !== false}
          onChange={(v) => updateConfig('allowPartialPayments', v)}
          label={p('allow_partial_payments')}
          cfgTheme={cfgTheme}
        />
        <ConfigCheckbox
          checked={facturationConfig.allowInstallments !== false}
          onChange={(v) => updateConfig('allowInstallments', v)}
          label={p('allow_installments')}
          cfgTheme={cfgTheme}
        />
      </div>
    </ConfigSection>
  );

  const receivablesTab = (
    <div className="space-y-6">
      <ConfigSection icon={AlertTriangle} title={p('tab_receivables')} hint={p('tab_receivables_hint')} cfgTheme={cfgTheme}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="space-y-2">
            <label style={cfgTheme.labelStyle}>{p('overdue_threshold_days')}</label>
            <input
              type="number"
              min={0}
              className={cfgTheme.input}
              value={facturationConfig.overdueThresholdDays ?? 30}
              onChange={(e) => updateConfig('overdueThresholdDays', parseInt(e.target.value, 10) || 0)}
            />
            <small className={cfgTheme.hint}>{p('overdue_threshold_hint')}</small>
          </div>
          <div className="flex flex-col gap-3 justify-center">
            <ConfigCheckbox
              checked={facturationConfig.requireTaxNumber === true}
              onChange={(v) => updateConfig('requireTaxNumber', v)}
              label={p('require_tax_number')}
              cfgTheme={cfgTheme}
            />
            <ConfigCheckbox
              checked={facturationConfig.requireAddress === true}
              onChange={(v) => updateConfig('requireAddress', v)}
              label={p('require_address')}
              cfgTheme={cfgTheme}
            />
            <ConfigCheckbox
              checked={facturationConfig.autoAssignClientNumber !== false}
              onChange={(v) => updateConfig('autoAssignClientNumber', v)}
              label={p('auto_assign_client_number')}
              cfgTheme={cfgTheme}
            />
          </div>
        </div>
      </ConfigSection>

      <ConfigSection icon={Receipt} title={p('client_config_title')} hint={p('client_discounts_hint')} cfgTheme={cfgTheme}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-2">
            <label style={cfgTheme.labelStyle}>{p('client_company_label')}</label>
            <CustomNumberInput
              min={0}
              max={100}
              step={0.01}
              decimals={2}
              suffix="%"
              value={facturationConfig.clientDiscounts?.company || ''}
              onChange={(value) => updateNested('clientDiscounts', 'company', parseFloat(value) || 0)}
            />
          </div>
          <div className="space-y-2">
            <label style={cfgTheme.labelStyle}>{p('client_individual_label')}</label>
            <CustomNumberInput
              min={0}
              max={100}
              step={0.01}
              decimals={2}
              suffix="%"
              value={facturationConfig.clientDiscounts?.individual || ''}
              onChange={(value) => updateNested('clientDiscounts', 'individual', parseFloat(value) || 0)}
            />
          </div>
          <div className="space-y-2">
            <label style={cfgTheme.labelStyle}>{p('client_institution_label')}</label>
            <CustomNumberInput
              min={0}
              max={100}
              step={0.01}
              decimals={2}
              suffix="%"
              value={facturationConfig.clientDiscounts?.institution || ''}
              onChange={(value) => updateNested('clientDiscounts', 'institution', parseFloat(value) || 0)}
            />
          </div>
        </div>
      </ConfigSection>
    </div>
  );

  const collectionTab = (
    <ConfigSection icon={Bell} title={p('tab_collection')} hint={p('tab_collection_hint')} cfgTheme={cfgTheme}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <ConfigCheckbox
          checked={facturationConfig.autoSendReminders === true}
          onChange={(v) => updateConfig('autoSendReminders', v)}
          label={p('auto_send_reminders')}
          cfgTheme={cfgTheme}
        />
        <div className="space-y-2">
          <label style={cfgTheme.labelStyle}>{p('reminder_interval_days')}</label>
          <input
            type="number"
            min={1}
            className={cfgTheme.input}
            value={facturationConfig.reminderIntervalDays ?? 7}
            onChange={(e) => updateConfig('reminderIntervalDays', parseInt(e.target.value, 10) || 7)}
          />
        </div>
        <div className="space-y-2">
          <label style={cfgTheme.labelStyle}>{p('max_reminders')}</label>
          <input
            type="number"
            min={1}
            className={cfgTheme.input}
            value={facturationConfig.maxReminders ?? 3}
            onChange={(e) => updateConfig('maxReminders', parseInt(e.target.value, 10) || 3)}
          />
        </div>
        <div className="space-y-2">
          <label style={cfgTheme.labelStyle}>{p('escalation_after_days')}</label>
          <input
            type="number"
            min={0}
            className={cfgTheme.input}
            value={facturationConfig.escalationAfterDays ?? 30}
            onChange={(e) => updateConfig('escalationAfterDays', parseInt(e.target.value, 10) || 0)}
          />
          <small className={cfgTheme.hint}>{p('escalation_after_hint')}</small>
        </div>
      </div>
    </ConfigSection>
  );

  const displayTab = (
    <ConfigSection icon={Eye} title={p('section_display')} hint={p('section_display_hint')} cfgTheme={cfgTheme}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <label style={cfgTheme.labelStyle}>{p('items_per_page')}</label>
          <select
            value={facturationConfig.itemsPerPage}
            onChange={(e) => updateConfig('itemsPerPage', parseInt(e.target.value, 10))}
            className={cfgTheme.input}
          >
            {[25, 50, 100].map((n) => (
              <option key={n} value={n} className="text-gray-900">{n}</option>
            ))}
          </select>
          <small className={cfgTheme.hint}>{p('items_per_page_hint')}</small>
        </div>
        <div className="space-y-2">
          <label style={cfgTheme.labelStyle}>{p('default_view')}</label>
          <select
            value={facturationConfig.defaultView || 'table'}
            onChange={(e) => updateConfig('defaultView', e.target.value)}
            className={cfgTheme.input}
          >
            <option value="table" className="text-gray-900">{p('view_table')}</option>
            <option value="cards" className="text-gray-900">{p('view_cards')}</option>
          </select>
          <small className={cfgTheme.hint}>{p('default_view_hint')}</small>
        </div>
      </div>
    </ConfigSection>
  );

  const tabs = [
    { id: 'documents', label: p('tab_documents'), icon: FileText },
    { id: 'payments', label: p('tab_payments'), icon: Wallet },
    { id: 'receivables', label: p('tab_receivables'), icon: AlertTriangle },
    { id: 'collection', label: p('tab_collection'), icon: Bell },
    { id: 'display', label: p('tab_display'), icon: Eye },
  ];

  const tabContent = {
    documents: documentsTab,
    payments: paymentsTab,
    receivables: receivablesTab,
    collection: collectionTab,
    display: displayTab,
  };

  return (
    <>
      <ModuleConfigPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        titleIcon={Settings}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabContent={tabContent}
        loading={loading}
        saving={saving}
        saved={saved}
        savedMessage={p('save_success')}
        saveError={saveError}
        onSave={handleSave}
        onReset={handleReset}
      />

      {showTableConfigModal && (
        <QawaemErpCrudModal
          open={showTableConfigModal}
          onClose={() => setShowTableConfigModal(false)}
          title={p('table_config')}
          icon={FileText}
          maxWidth="900px"
          testId="table-config-modal"
        >
          <div className="space-y-6">
            <div>
              <label className={`flex w-full items-center justify-end gap-2 text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-slate-600' : 'text-white/70'}`}>
                <Table size={18} className="text-blue-400" />
                {p('table_type_label')}
              </label>
              <CustomSelect
                value={facturationConfig.tableType || 'standard'}
                onChange={(value) => updateConfig('tableType', value)}
                options={[
                  { value: 'standard', label: p('table_type_standard') },
                  { value: 'simplifiee', label: p('table_type_simplifiee') },
                ]}
              />
            </div>
            <div>
              <label className={`flex w-full items-center justify-end gap-2 text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-slate-600' : 'text-white/70'}`}>
                <Truck size={18} className="text-blue-400" />
                {p('default_unit_label')}
              </label>
              <input
                type="text"
                className={cfgTheme.input}
                value={facturationConfig.defaultUnit || ''}
                onChange={(e) => updateConfig('defaultUnit', e.target.value)}
                placeholder={p('placeholder_default_unit')}
              />
            </div>
            <button
              type="button"
              onClick={() => updateConfig('showReferenceColumn', !(facturationConfig.showReferenceColumn !== false))}
              className="flex items-center gap-2 transition-colors cursor-pointer"
              style={{ color: cfgTheme.ink?.primary || 'inherit' }}
            >
              {facturationConfig.showReferenceColumn !== false
                ? <CheckSquare size={18} className="text-blue-400" />
                : <Square size={18} className="text-blue-400" />}
              <span>{p('show_reference_column')}</span>
            </button>
            <div className={`p-4 rounded-xl border overflow-x-auto ${isDashboardLightMode ? 'bg-slate-50 border-slate-200' : 'bg-white/5 border-white/10'}`}>
              <table className={`w-full text-sm ${isDashboardLightMode ? 'text-slate-800' : 'text-white/90'}`}>
                <thead className={isDashboardLightMode ? 'bg-slate-100 text-xs uppercase text-slate-700' : 'bg-white/10 text-xs uppercase'}>
                  <tr>
                    {[p('col_no'), ...(facturationConfig.showReferenceColumn !== false ? [p('col_reference')] : []), p('col_designation'), p('col_unit'), p('col_qty'), p('col_unit_price_ht'), p('col_amount')].map((header) => (
                      <th key={header} className="px-3 py-2 text-left">{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className={isDashboardLightMode ? 'border-b border-slate-200' : 'border-b border-white/10'}>
                    {['1', ...(facturationConfig.showReferenceColumn !== false ? ['REF001'] : []), p('preview_sample_demo_product'), facturationConfig.defaultUnit, '2', '1000.00', '2000.00'].map((cell, idx) => (
                      <td key={idx} className="px-3 py-2">{cell}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            <div className={`flex items-start gap-3 text-xs p-4 rounded-xl border ${isDashboardLightMode ? 'text-slate-600 bg-blue-50 border-blue-100' : 'text-white/60 bg-blue-500/5 border-blue-500/10'}`}>
              <LayoutGrid className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-400" />
              <p>{p('templates_apply_new_only')}</p>
            </div>
          </div>
          <div className="flex justify-end pt-6">
            <button
              type="button"
              onClick={() => setShowTableConfigModal(false)}
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
            >
              <CheckCircle size={18} />
              {p('apply')}
            </button>
          </div>
        </QawaemErpCrudModal>
      )}
    </>
  );
};

export default FacturationConfigPage;
